
import { EVENT_ADD_BUCKET } from 'routes/trial/interfaces'
import { firstValueFrom } from 'rxjs'
import { map } from 'rxjs/operators'
import { connectedDestinationsSelector } from 'services/destinations'
import { HS_EVENT_BUCKET_CREATED, trackHubspotEvent } from 'services/tracking/hubspot'
import { trackTrainingEvent } from 'services/users/actions'
import { currentUserSelector } from 'services/users/selectors'
import { bucketAdapter } from 'shared/adapters'
import { Bucket, FBAlbum, PinterestBoard } from 'shared/types'
import { getRandomElements, sortByKeyAlphabetically, sortByKeyAscending } from 'shared/utils'
import StoreState, { createAppAsyncThunk, getState, StoreThunkDispatch } from 'store/state'
import { bucketColorsPreset } from 'utils/colors'
import toCamelCase from 'utils/toCamelCase'
import { autoPostSlotAdapter, postAdapter } from '../adapters/posts'
import { bucketGroupsSelector, bucketPresetsSelector, bucketsMapSelector, bucketsSelector } from '../selectors'
import { DeleteMethod, V1 } from './net'
import { ContentType, PostDestination } from 'shared'

export { DeleteMethod }

export const getBuckets = createAppAsyncThunk(
  'posts/buckets/get',
  async (force: boolean = true, { dispatch, getState, rejectWithValue }) => {
    if (!force) {
      const storedBuckets = bucketsSelector(getState() as StoreState)
      const storedGroups = bucketGroupsSelector(getState() as StoreState)
      if (storedBuckets.length > 0 && storedGroups.length > 0) {
        return { buckets: storedBuckets, groups: storedGroups }
      }
    }
    try {
      const buckets = await firstValueFrom(dispatch(V1.getBuckets())).then(response => response.data.map(bucketAdapter))
      const groups = await firstValueFrom(dispatch(V1.getBucketGroups())).then(response => {
        return response.data
          .sort(sortByKeyAlphabetically('name'))
          .map((g: any) => {
            const groupId = g.id.toString()
            const groupBuckets = buckets.filter(b => b.groupId === groupId).sort(sortByKeyAscending('order'))
            const groupBucketsMap = new Map(groupBuckets.map(b => [b.id, b]))
            return {
              ...g,
              id: groupId,
              bucketIds: [...groupBucketsMap.keys()],
              buckets: groupBucketsMap,
              order: parseInt(g.order, 10)
            }
          })
      })
      return { buckets, groups }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const getBucketById = createAppAsyncThunk(
  'posts/buckets/getById',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(V1.getBucketById(id)))
      return bucketAdapter(response.data[0])
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

type CreateBucketParams = {
  name: string,
  color: string,
  description: string,
  isPublic: boolean,
  ppids: string[],
  activeFrom?: string,
  activeTo?: string,
  groupId: string | null,
  order?: number
}
export const createBucket = createAppAsyncThunk(
  'posts/buckets/create',
  async (params: CreateBucketParams, { dispatch, rejectWithValue }) => {
    const { name, color, description, isPublic, ppids, activeFrom, activeTo, groupId, order } = params
    try {
      const response = await firstValueFrom(
        dispatch(V1.createBucket(name, color, description, isPublic, ppids, activeFrom, activeTo, groupId, order))
      )
      const bucket = bucketAdapter(response)
      dispatch(trackTrainingEvent({ eventCode: EVENT_ADD_BUCKET }))
      dispatch(trackHubspotEvent(HS_EVENT_BUCKET_CREATED))
      return bucket
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

type UpdateBucketParams = {
  id: string,
  name: string,
  color: string,
  description: string,
  isPublic: boolean,
  ppids: string[],
  copyFromPpid: string | undefined,
  activeFrom: string | undefined,
  activeTo: string | undefined,
  fbAlbums?: FBAlbum[],
  piBoards?: PinterestBoard[],
  groupId: string | null
  order?: number
}
export const updateBucket = createAppAsyncThunk(
  'posts/buckets/update',
  async (params: UpdateBucketParams, { dispatch, getState, rejectWithValue }) => {
    const {
      id, name, color, description, isPublic, ppids, copyFromPpid,
      activeFrom, activeTo, fbAlbums, piBoards, groupId, order
    } = params
    try {
      const response = await firstValueFrom(dispatch(V1.updateBucket(
        id, name, color, description, isPublic, ppids, copyFromPpid, activeFrom, activeTo, fbAlbums, piBoards, groupId, order
      )))
      const bucket = bucketAdapter(response)
      const profiles = connectedDestinationsSelector(getState() as StoreState)
      return {
        ...response,
        bucket,
        profiles: Object.values(profiles).filter(p => bucket.ppids.includes(p.ppid))
      } as { bucket: Bucket, profiles: PostDestination[] } & any
    } catch (e: any) {
      return rejectWithValue(toCamelCase(e.response?.error || e))
    }
  }
)

export const deleteBucket = createAppAsyncThunk(
  'posts/buckets/delete',
  async (params: { id: string, method: DeleteMethod, targetBucketId?: string | null }, { dispatch, rejectWithValue }) => {
    const { id, method, targetBucketId } = params
    try {
      await firstValueFrom(dispatch(V1.deleteBucket(id, method, targetBucketId)))
      return id
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export function getBucketPosts(
  bucketId: string,
  page: number,
  ppid: string,
  query?: string,
  types?: ContentType[],
  searchInSummary?: boolean
) {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const buckets = bucketsMapSelector(getState())
    const isBeta = currentUserSelector(getState() as StoreState)?.roles?.isBeta
    const bucket = buckets[bucketId]
    return dispatch(V1.getBucketPosts(bucketId, page, ppid, query, types, searchInSummary, isBeta)).pipe(
      map(response => ({
        posts: response.posts.map((p, index) => {
          const post = postAdapter(p, index)
          return { ...post, bucket }
        }),
        slots: response.posts.filter(p => p.autoPost && !p.isVirtual && p.id !== 'empty' && p.time).map(autoPostSlotAdapter),
        page,
        totalPostsCount: response.postsCount,
        paging: response.paging,
        request: { query, types, searchInSummary, planned: true }
      })))
  }
}

export const reorderBuckets = createAppAsyncThunk(
  'posts/buckets/reorder',
  async (ids: string[], { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(V1.reorderBuckets(ids)))
      return ids
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const getDefaultBuckets = createAppAsyncThunk(
  'posts/buckets/getDefault',
  async (force: boolean = true, { dispatch, getState, rejectWithValue }) => {
    if (!force) {
      const cached = bucketPresetsSelector(getState() as StoreState)
      if (cached.length > 0) {
        return cached
      }
    }
    try {
      const response = await firstValueFrom(dispatch(V1.getDefaultBuckets()))
      return response.data.map((bucket: any) => ({
        ...bucket,
        id: bucket.id.toString(),
        name: bucket.name.trim(),
        isDefault: parseInt(bucket.isPreselected, 10) === 1,
        color: bucket.color || getRandomElements(bucketColorsPreset, 1)[0]
      }))
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const createBucketGroup = createAppAsyncThunk(
  'posts/buckets/groups/create',
  async (params: { name: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(V1.saveBucketGroup(params.name)))
      return response
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const updateBucketGroup = createAppAsyncThunk(
  'posts/buckets/groups/update',
  async (params: { id: string, name: string }, { dispatch, rejectWithValue }) => {
    const { id, name } = params
    try {
      const response = await firstValueFrom(dispatch(V1.saveBucketGroup(name, id)))
      return response
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const deleteBucketGroup = createAppAsyncThunk(
  'posts/buckets/groups/delete',
  async (id: string, { dispatch, rejectWithValue }) => {
    try {
      await firstValueFrom(dispatch(V1.deleteBucketGroup(id)))
      return id
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)
